import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  Plus, Trash2, Briefcase, DollarSign, Receipt, Bot, Sparkles, 
  TrendingUp, TrendingDown, Building, ShoppingCart, Truck, Edit, Loader2
} from "lucide-react";

const BUSINESS_TYPES = [
  "Retail", "Restaurant", "Consulting", "Freelance", "E-commerce", 
  "Real Estate", "Construction", "Healthcare", "Technology", "Other"
];

const EXPENSE_CATEGORIES = [
  { value: "supplies", label: "Supplies" },
  { value: "equipment", label: "Equipment" },
  { value: "rent", label: "Rent" },
  { value: "utilities", label: "Utilities" },
  { value: "payroll", label: "Payroll" },
  { value: "marketing", label: "Marketing" },
  { value: "travel", label: "Travel" },
  { value: "professional_services", label: "Professional Services" },
  { value: "insurance", label: "Insurance" },
  { value: "taxes", label: "Taxes" },
  { value: "other", label: "Other" },
];

const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function Business() {
  const { api } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bizDialogOpen, setBizDialogOpen] = useState(false);
  const [editBizDialogOpen, setEditBizDialogOpen] = useState(false);
  const [editingBiz, setEditingBiz] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [acquisitionDialogOpen, setAcquisitionDialogOpen] = useState(false);
  const [acquisitionLoading, setAcquisitionLoading] = useState(false);
  const [acquisitionResult, setAcquisitionResult] = useState(null);
  const [acquisitionForm, setAcquisitionForm] = useState({
    industry: "any",
    budget_min: 50000,
    budget_max: 500000,
    location: "any"
  });
  const [valuationDialogOpen, setValuationDialogOpen] = useState(false);
  const [valuationLoading, setValuationLoading] = useState(false);
  const [valuationResult, setValuationResult] = useState(null);
  
  const [bizForm, setBizForm] = useState({ name: "", business_type: "", description: "" });
  const [incomeForm, setIncomeForm] = useState({ source: "", amount: "", category: "sales", description: "", invoice_number: "" });
  const [expenseForm, setExpenseForm] = useState({ vendor: "", amount: "", category: "", description: "", receipt_number: "", tax_deductible: true });

  useEffect(() => { fetchBusinesses(); }, []);

  useEffect(() => {
    if (selectedBiz) {
      fetchBusinessData(selectedBiz.id);
    }
  }, [selectedBiz]);

  const fetchBusinesses = async () => {
    try {
      const res = await api.get("/business");
      setBusinesses(res.data);
      if (res.data.length > 0 && !selectedBiz) {
        setSelectedBiz(res.data[0]);
      }
    } catch (e) { toast.error("Failed to load businesses"); }
    finally { setLoading(false); }
  };

  const fetchBusinessData = async (bizId) => {
    try {
      const [incRes, expRes, sumRes] = await Promise.all([
        api.get(`/business/income?business_id=${bizId}`),
        api.get(`/business/expenses?business_id=${bizId}`),
        api.get(`/business/${bizId}/summary`)
      ]);
      setIncome(incRes.data);
      setExpenses(expRes.data);
      setSummary(sumRes.data);
    } catch (e) { console.error(e); }
  };

  const runAcquisitionSearch = async () => {
    setAcquisitionLoading(true);
    toast.info("🔍 Searching for business opportunities...");
    try {
      const res = await api.post("/ai/business-acquisition", acquisitionForm);
      setAcquisitionResult(res.data);
      toast.success("✅ Found business opportunities!");
    } catch (e) {
      toast.error("Failed to search businesses");
    } finally {
      setAcquisitionLoading(false);
    }
  };

  const runValuation = async () => {
    if (!selectedBiz || !summary) {
      toast.error("Select a business first");
      return;
    }
    setValuationLoading(true);
    toast.info("📊 Calculating business valuation...");
    try {
      const res = await api.post("/ai/business-valuation", {
        business_name: selectedBiz.name,
        annual_revenue: summary.total_income || 0,
        annual_profit: summary.net_profit || 0,
        years_in_business: 3,
        industry: selectedBiz.business_type,
        employees: 1,
        assets_value: 0
      });
      setValuationResult(res.data);
      setValuationDialogOpen(true);
      toast.success("✅ Valuation complete!");
    } catch (e) {
      toast.error("Failed to value business");
    } finally {
      setValuationLoading(false);
    }
  };

  const handleAddBusiness = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post("/business", bizForm);
      toast.success("Business added");
      setBizDialogOpen(false);
      setBizForm({ name: "", business_type: "", description: "" });
      fetchBusinesses();
      setSelectedBiz(res.data);
    } catch (e) { toast.error("Failed to add business"); }
    finally { setSubmitting(false); }
  };

  const handleEditBusiness = (biz) => {
    setEditingBiz(biz);
    setBizForm({
      name: biz.name || "",
      business_type: biz.business_type || "",
      description: biz.description || ""
    });
    setEditBizDialogOpen(true);
  };

  const handleUpdateBusiness = async (e) => {
    e.preventDefault();
    if (submitting || !editingBiz) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/business/${editingBiz.id}`, bizForm);
      toast.success("Business updated");
      setEditBizDialogOpen(false);
      setEditingBiz(null);
      setBizForm({ name: "", business_type: "", description: "" });
      fetchBusinesses();
      if (selectedBiz?.id === editingBiz.id) {
        setSelectedBiz(res.data);
      }
    } catch (e) { toast.error("Failed to update business"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteBusiness = async (bizId) => {
    if (!window.confirm("Delete this business? All income and expenses will also be deleted.")) return;
    try {
      await api.delete(`/business/${bizId}`);
      toast.success("Business deleted");
      if (selectedBiz?.id === bizId) setSelectedBiz(null);
      fetchBusinesses();
    } catch (e) { toast.error("Failed to delete business"); }
  };

  const handleAddIncome = async (e) => {
    e.preventDefault();
    if (!selectedBiz) return;
    try {
      await api.post("/business/income", { ...incomeForm, business_id: selectedBiz.id, amount: parseFloat(incomeForm.amount) });
      toast.success("Income added");
      setIncomeDialogOpen(false);
      setIncomeForm({ source: "", amount: "", category: "sales", description: "", invoice_number: "" });
      fetchBusinessData(selectedBiz.id);
    } catch (e) { toast.error("Failed to add income"); }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!selectedBiz) return;
    try {
      await api.post("/business/expenses", { ...expenseForm, business_id: selectedBiz.id, amount: parseFloat(expenseForm.amount) });
      toast.success("Expense added");
      setExpenseDialogOpen(false);
      setExpenseForm({ vendor: "", amount: "", category: "", description: "", receipt_number: "", tax_deductible: true });
      fetchBusinessData(selectedBiz.id);
    } catch (e) { toast.error("Failed to add expense"); }
  };

  const handleBookkeepingAdvice = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/ai/bookkeeping-advice");
      setAiResult(res.data.analysis);
    } catch (e) { toast.error("AI analysis failed"); }
    finally { setAiLoading(false); }
  };

  const handleDeleteIncome = async (id) => {
    if (!window.confirm("Delete this income?")) return;
    try {
      await api.delete(`/business/income/${id}`);
      toast.success("Deleted");
      fetchBusinessData(selectedBiz.id);
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/business/expenses/${id}`);
      toast.success("Deleted");
      fetchBusinessData(selectedBiz.id);
    } catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <Layout title="Business Management">
      <div className="space-y-6">
        {/* Business Selector & Add */}
        <div className="flex flex-wrap items-center gap-4">
          <Select value={selectedBiz?.id || ""} onValueChange={(v) => setSelectedBiz(businesses.find(b => b.id === v))}>
            <SelectTrigger className="w-64 bg-[#0A0A0A] border-white/10 rounded-sm" data-testid="business-select">
              <SelectValue placeholder="Select business" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              {businesses.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedBiz && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleEditBusiness(selectedBiz)}
                className="text-zinc-400 hover:text-primary"
                data-testid="edit-business-btn"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDeleteBusiness(selectedBiz.id)}
                className="text-zinc-400 hover:text-red-500"
                data-testid="delete-business-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}

          <Dialog open={bizDialogOpen} onOpenChange={setBizDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black font-bold rounded-sm" data-testid="add-business-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Business
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
              <DialogHeader><DialogTitle className="text-white">Add Business</DialogTitle></DialogHeader>
              <form onSubmit={handleAddBusiness} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Business Name</Label>
                  <Input value={bizForm.name} onChange={(e) => setBizForm({...bizForm, name: e.target.value})} required className="bg-black/50 border-white/10 rounded-sm" data-testid="business-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Business Type</Label>
                  <Select value={bizForm.business_type} onValueChange={(v) => setBizForm({...bizForm, business_type: v})} required>
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Description</Label>
                  <Input value={bizForm.description} onChange={(e) => setBizForm({...bizForm, description: e.target.value})} className="bg-black/50 border-white/10 rounded-sm" placeholder="Brief description (optional)" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-primary text-black font-bold rounded-sm">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Add Business
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Business Dialog */}
          <Dialog open={editBizDialogOpen} onOpenChange={setEditBizDialogOpen}>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
              <DialogHeader><DialogTitle className="text-white">Edit Business</DialogTitle></DialogHeader>
              <form onSubmit={handleUpdateBusiness} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Business Name</Label>
                  <Input value={bizForm.name} onChange={(e) => setBizForm({...bizForm, name: e.target.value})} required className="bg-black/50 border-white/10 rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Business Type</Label>
                  <Select value={bizForm.business_type} onValueChange={(v) => setBizForm({...bizForm, business_type: v})} required>
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Description</Label>
                  <Input value={bizForm.description} onChange={(e) => setBizForm({...bizForm, description: e.target.value})} className="bg-black/50 border-white/10 rounded-sm" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-primary text-black font-bold rounded-sm">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Update Business
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-sm" data-testid="ai-bookkeeping-btn">
                <Bot className="w-4 h-4 mr-2" /> AI Bookkeeping Advice
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-violet-500/30 rounded-none max-w-2xl ai-active">
              <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-500" /> AI Bookkeeping Assistant</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <Button onClick={handleBookkeepingAdvice} disabled={aiLoading} className="w-full bg-violet-500 text-white hover:bg-violet-600 rounded-sm">
                  {aiLoading ? "Analyzing..." : "Get Bookkeeping Advice"}
                </Button>
                {aiResult && (
                  <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-sm max-h-96 overflow-y-auto">
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap">{aiResult}</div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* AI Business Acquisition Bot */}
          <Dialog open={acquisitionDialogOpen} onOpenChange={setAcquisitionDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 rounded-sm" data-testid="ai-acquisition-btn">
                <ShoppingCart className="w-4 h-4 mr-2" /> AI Acquisition Bot
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-emerald-500/30 rounded-none max-w-3xl ai-active">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-emerald-500" /> AI Business Acquisition Bot
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-zinc-400">Search for businesses to acquire based on your criteria. Our AI will analyze opportunities and provide detailed guidance.</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Industry</Label>
                    <Select value={acquisitionForm.industry} onValueChange={(v) => setAcquisitionForm({...acquisitionForm, industry: v})}>
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="acquisition-industry">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="any">Any Industry</SelectItem>
                        {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Location</Label>
                    <Input 
                      value={acquisitionForm.location} 
                      onChange={(e) => setAcquisitionForm({...acquisitionForm, location: e.target.value})}
                      placeholder="e.g., California, Texas, or 'any'"
                      className="bg-black/50 border-white/10 rounded-sm"
                      data-testid="acquisition-location"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Min Budget ($)</Label>
                    <Input 
                      type="number"
                      value={acquisitionForm.budget_min} 
                      onChange={(e) => setAcquisitionForm({...acquisitionForm, budget_min: parseFloat(e.target.value) || 0})}
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                      data-testid="acquisition-budget-min"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Max Budget ($)</Label>
                    <Input 
                      type="number"
                      value={acquisitionForm.budget_max} 
                      onChange={(e) => setAcquisitionForm({...acquisitionForm, budget_max: parseFloat(e.target.value) || 0})}
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                      data-testid="acquisition-budget-max"
                    />
                  </div>
                </div>

                <Button 
                  onClick={runAcquisitionSearch} 
                  disabled={acquisitionLoading} 
                  className="w-full bg-emerald-500 text-white hover:bg-emerald-600 rounded-sm"
                  data-testid="run-acquisition-search-btn"
                >
                  {acquisitionLoading ? (
                    <>
                      <Bot className="w-4 h-4 mr-2 animate-spin" /> Searching...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" /> Find Business Opportunities
                    </>
                  )}
                </Button>

                {acquisitionResult && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <span>Search: {acquisitionResult.search_criteria?.industry || 'Any'} industry</span>
                      <span>•</span>
                      <span>{formatCurrency(acquisitionResult.search_criteria?.budget_min || 0)} - {formatCurrency(acquisitionResult.search_criteria?.budget_max || 0)}</span>
                      <span>•</span>
                      <span>{acquisitionResult.search_criteria?.location || 'Any location'}</span>
                    </div>
                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-sm max-h-[50vh] overflow-y-auto">
                      <div className="text-sm text-zinc-300 whitespace-pre-wrap">{acquisitionResult.analysis}</div>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* AI Business Valuation */}
          <Button 
            onClick={runValuation} 
            disabled={valuationLoading || !selectedBiz}
            variant="outline" 
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 rounded-sm disabled:opacity-50" 
            data-testid="ai-valuation-btn"
          >
            {valuationLoading ? (
              <>
                <Bot className="w-4 h-4 mr-2 animate-spin" /> Valuing...
              </>
            ) : (
              <>
                <TrendingUp className="w-4 h-4 mr-2" /> AI Valuation
              </>
            )}
          </Button>

          {/* Valuation Results Dialog */}
          <Dialog open={valuationDialogOpen} onOpenChange={setValuationDialogOpen}>
            <DialogContent className="bg-[#0A0A0A] border-amber-500/30 rounded-none max-w-3xl ai-active">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-500" /> Business Valuation Report
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {selectedBiz && summary && (
                  <div className="grid grid-cols-3 gap-3 p-3 bg-white/5 border border-white/10 rounded-sm">
                    <div>
                      <p className="text-xs text-zinc-500 uppercase">Business</p>
                      <p className="text-white font-medium">{selectedBiz.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase">Annual Revenue</p>
                      <p className="text-primary font-mono">{formatCurrency(summary.total_income || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase">Net Profit</p>
                      <p className={`font-mono ${(summary.net_profit || 0) >= 0 ? 'text-primary' : 'text-red-500'}`}>
                        {formatCurrency(summary.net_profit || 0)}
                      </p>
                    </div>
                  </div>
                )}
                {valuationResult && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm max-h-[50vh] overflow-y-auto">
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap">{valuationResult.analysis || valuationResult.valuation}</div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-4">
                <p className="text-xs font-mono uppercase text-zinc-500 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-primary font-mono">{formatCurrency(summary.total_income)}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-4">
                <p className="text-xs font-mono uppercase text-zinc-500 mb-1">Total Expenses</p>
                <p className="text-2xl font-bold text-red-500 font-mono">{formatCurrency(summary.total_expenses)}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-4">
                <p className="text-xs font-mono uppercase text-zinc-500 mb-1">Net Profit</p>
                <p className={`text-2xl font-bold font-mono ${summary.net_profit >= 0 ? 'text-primary' : 'text-red-500'}`}>
                  {formatCurrency(summary.net_profit)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardContent className="p-4">
                <p className="text-xs font-mono uppercase text-zinc-500 mb-1">Tax Deductible</p>
                <p className="text-2xl font-bold text-amber-500 font-mono">{formatCurrency(summary.tax_deductible_expenses)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Income & Expenses Tabs */}
        {selectedBiz && (
          <Tabs defaultValue="income" className="w-full">
            <TabsList className="bg-[#0A0A0A] border border-white/10 rounded-none">
              <TabsTrigger value="income" className="rounded-none data-[state=active]:bg-white/10">
                <DollarSign className="w-4 h-4 mr-2" /> Income
              </TabsTrigger>
              <TabsTrigger value="expenses" className="rounded-none data-[state=active]:bg-white/10">
                <Receipt className="w-4 h-4 mr-2" /> Expenses
              </TabsTrigger>
            </TabsList>

            <TabsContent value="income" className="mt-4">
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg text-white">Business Income</CardTitle>
                  <Dialog open={incomeDialogOpen} onOpenChange={setIncomeDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary text-black font-bold rounded-sm" data-testid="add-business-income-btn">
                        <Plus className="w-4 h-4 mr-2" /> Add Income
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
                      <DialogHeader><DialogTitle className="text-white">Add Business Income</DialogTitle></DialogHeader>
                      <form onSubmit={handleAddIncome} className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Source/Client</Label>
                          <Input value={incomeForm.source} onChange={(e) => setIncomeForm({...incomeForm, source: e.target.value})} required className="bg-black/50 border-white/10 rounded-sm" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Amount</Label>
                          <Input type="number" step="0.01" value={incomeForm.amount} onChange={(e) => setIncomeForm({...incomeForm, amount: e.target.value})} required className="bg-black/50 border-white/10 rounded-sm font-mono" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Invoice # (Optional)</Label>
                          <Input value={incomeForm.invoice_number} onChange={(e) => setIncomeForm({...incomeForm, invoice_number: e.target.value})} className="bg-black/50 border-white/10 rounded-sm" />
                        </div>
                        <Button type="submit" className="w-full bg-primary text-black font-bold rounded-sm">Add Income</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {income.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">No income recorded yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {income.map((inc) => (
                        <div key={inc.id} className="flex items-center justify-between p-4 border border-white/5 hover:border-white/10">
                          <div>
                            <p className="text-white font-medium">{inc.source}</p>
                            <p className="text-sm text-zinc-500">{inc.date} {inc.invoice_number && `• Invoice: ${inc.invoice_number}`}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <p className="text-lg font-bold text-primary font-mono">+{formatCurrency(inc.amount)}</p>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteIncome(inc.id)} className="text-zinc-500 hover:text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="expenses" className="mt-4">
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg text-white">Business Expenses</CardTitle>
                  <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary text-black font-bold rounded-sm" data-testid="add-business-expense-btn">
                        <Plus className="w-4 h-4 mr-2" /> Add Expense
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
                      <DialogHeader><DialogTitle className="text-white">Add Business Expense</DialogTitle></DialogHeader>
                      <form onSubmit={handleAddExpense} className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Vendor</Label>
                          <Input value={expenseForm.vendor} onChange={(e) => setExpenseForm({...expenseForm, vendor: e.target.value})} required className="bg-black/50 border-white/10 rounded-sm" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Category</Label>
                          <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})} required>
                            <SelectTrigger className="bg-black/50 border-white/10 rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/10">
                              {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Amount</Label>
                          <Input type="number" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})} required className="bg-black/50 border-white/10 rounded-sm font-mono" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Description</Label>
                          <Input value={expenseForm.description} onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})} className="bg-black/50 border-white/10 rounded-sm" />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10">
                          <span className="text-sm text-zinc-300">Tax Deductible</span>
                          <Switch checked={expenseForm.tax_deductible} onCheckedChange={(c) => setExpenseForm({...expenseForm, tax_deductible: c})} />
                        </div>
                        <Button type="submit" className="w-full bg-primary text-black font-bold rounded-sm">Add Expense</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {expenses.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">No expenses recorded yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {expenses.map((exp) => (
                        <div key={exp.id} className="flex items-center justify-between p-4 border border-white/5 hover:border-white/10">
                          <div>
                            <p className="text-white font-medium">{exp.vendor}</p>
                            <p className="text-sm text-zinc-500">{exp.category} • {exp.date} {exp.tax_deductible && <span className="text-amber-500">• Tax Deductible</span>}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <p className="text-lg font-bold text-red-500 font-mono">-{formatCurrency(exp.amount)}</p>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteExpense(exp.id)} className="text-zinc-500 hover:text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {!selectedBiz && !loading && (
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-12 text-center">
              <Briefcase className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No businesses yet. Add your first business to get started!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
